import * as THREE from 'three';
import type { GameClient } from '../../app/gameClient';
import { Cat, KINDS } from '../../gameplay/registry';
import { C } from '../../gameplay/types';
import { useApp } from '../../ui/store';
import { canCapture } from '../simulation/systems/capture';

/**
 * Mouse and keyboard handling: selection (click, box, shift, double-click),
 * context commands, targeting modes, control groups, camera controls and hotkeys.
 */
/** A small planted flag, the cursor for commandeering a building. */
const CAPTURE_CURSOR = `url("data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path d="M5 2v20" stroke="#222" stroke-width="3"/><path d="M5 2v20" stroke="#f3d98b" stroke-width="1.5"/><path d="M6 3h13l-3 4 3 4H6z" fill="#e8c45a" stroke="#222" stroke-width="1"/></svg>')}") 4 22, cell`;

export class InputController {
  private keys = new Set<string>();
  private mouse = { x: 0, y: 0, inside: false, inWindow: false, overControl: false };
  private dragStart: { x: number; y: number } | null = null;
  private panDrag: { x: number; y: number } | null = null;
  private box: HTMLDivElement;
  private lastClick = { t: 0, slot: -1 };
  private lastGroupTap = { i: -1, t: 0 };
  private hoverTimer = 0;
  private listeners: [EventTarget, string, EventListener, AddEventListenerOptions?][] = [];

  constructor(private client: GameClient) {
    const canvas = client.canvas;
    this.box = document.createElement('div');
    this.box.className = 'selection-box';
    this.box.style.display = 'none';
    canvas.parentElement?.appendChild(this.box);
    this.on(canvas, 'mousedown', (e) => this.mouseDown(e as MouseEvent));
    this.on(window, 'mousemove', (e) => this.mouseMove(e as MouseEvent));
    this.on(window, 'mouseup', (e) => this.mouseUp(e as MouseEvent));
    this.on(canvas, 'contextmenu', (e) => e.preventDefault());
    this.on(canvas, 'wheel', (e) => {
      e.preventDefault();
      const we = e as WheelEvent;
      this.client.cam.zoom(we.deltaY * (we.deltaMode === 1 ? 30 : 1));
    }, { passive: false });
    this.on(canvas, 'mouseenter', () => (this.mouse.inside = true));
    this.on(canvas, 'mouseleave', () => (this.mouse.inside = false));
    // Edge scrolling tracks the pointer across the whole window (the HUD bars sit over the canvas).
    this.on(document, 'mouseleave', () => (this.mouse.inWindow = false));
    this.on(window, 'keydown', (e) => this.keyDown(e as KeyboardEvent));
    this.on(window, 'keyup', (e) => this.keys.delete((e as KeyboardEvent).code));
    this.on(window, 'blur', () => this.keys.clear());
    this.on(window, 'resize', () => this.client.resize());
  }

  private on(t: EventTarget, type: string, fn: EventListener, opts?: AddEventListenerOptions): void {
    t.addEventListener(type, fn, opts);
    this.listeners.push([t, type, fn, opts]);
  }

  dispose(): void {
    for (const [t, type, fn, opts] of this.listeners) t.removeEventListener(type, fn, opts);
    this.box.remove();
  }

  private rect(): DOMRect {
    return this.client.canvas.getBoundingClientRect();
  }

  private ndc(x: number, y: number): [number, number] {
    const r = this.rect();
    return [((x - r.left) / r.width) * 2 - 1, -((y - r.top) / r.height) * 2 + 1];
  }

  private ground(x: number, y: number): THREE.Vector3 | null {
    const [nx, ny] = this.ndc(x, y);
    return this.client.groundUnderMouse(nx, ny);
  }

  private pickAt(x: number, y: number): number {
    const r = this.rect();
    return this.client.renderer.pick(x - r.left, y - r.top, r.width, r.height);
  }

  private mouseDown(e: MouseEvent): void {
    const c = this.client;
    if (e.button === 1) {
      this.panDrag = { x: e.clientX, y: e.clientY };
      e.preventDefault();
      return;
    }
    if (e.button === 2) {
      if (c.mode !== 'normal') {
        c.cancelMode();
        return;
      }
      const g = this.ground(e.clientX, e.clientY);
      if (!g) return;
      c.rightClick(g.x, g.z, this.pickAt(e.clientX, e.clientY), e.shiftKey);
      return;
    }
    if (e.button === 0) {
      if (e.altKey && c.transport.networked) {
        // Alt+click: flare for allies (ally request).
        const g = this.ground(e.clientX, e.clientY);
        if (g) c.chat('Requesting help here!', true, { x: g.x, z: g.z });
        return;
      }
      if (c.mode !== 'normal') {
        const g = this.ground(e.clientX, e.clientY);
        if (g) c.modeClick(g.x, g.z, this.pickAt(e.clientX, e.clientY), e.shiftKey);
        return;
      }
      this.dragStart = { x: e.clientX, y: e.clientY };
    }
  }

  private mouseMove(e: MouseEvent): void {
    this.mouse.x = e.clientX;
    this.mouse.y = e.clientY;
    this.mouse.inWindow = true;
    this.mouse.overControl = !!(e.target as Element | null)?.closest?.('button, select, input');
    const c = this.client;
    if (this.panDrag) {
      const dx = e.clientX - this.panDrag.x;
      const dy = e.clientY - this.panDrag.y;
      this.panDrag = { x: e.clientX, y: e.clientY };
      c.cam.pan(-dx * 0.05, -dy * 0.05);
      return;
    }
    if (c.mode === 'place') {
      const g = this.ground(e.clientX, e.clientY);
      if (g) c.updatePlacement(g.x, g.z);
    }
    if (this.dragStart) {
      const r = this.rect();
      const x0 = Math.min(this.dragStart.x, e.clientX) - r.left;
      const y0 = Math.min(this.dragStart.y, e.clientY) - r.top;
      const w = Math.abs(e.clientX - this.dragStart.x);
      const h = Math.abs(e.clientY - this.dragStart.y);
      if (w > 4 || h > 4) {
        this.box.style.display = 'block';
        this.box.style.left = `${x0}px`;
        this.box.style.top = `${y0}px`;
        this.box.style.width = `${w}px`;
        this.box.style.height = `${h}px`;
      }
    }
  }

  private mouseUp(e: MouseEvent): void {
    if (e.button === 1) {
      this.panDrag = null;
      return;
    }
    if (e.button !== 0 || !this.dragStart) return;
    const c = this.client;
    const w = c.world;
    const start = this.dragStart;
    this.dragStart = null;
    this.box.style.display = 'none';
    const dx = Math.abs(e.clientX - start.x);
    const dy = Math.abs(e.clientY - start.y);
    if (dx < 5 && dy < 5) {
      const s = this.pickAt(e.clientX, e.clientY);
      const now = performance.now();
      if (s < 0) {
        if (!e.shiftKey) c.setSelection([]);
        return;
      }
      const id = w.idOf(s);
      if (now - this.lastClick.t < 350 && this.lastClick.slot === s) {
        // Double click: all visible units of the same kind owned by the same player.
        const same = c.renderer.visibleSlots.filter((o) => w.alive[o] && w.kind[o] === w.kind[s] && w.owner[o] === w.owner[s]).map((o) => w.idOf(o));
        c.setSelection(same, e.shiftKey);
      } else if (e.shiftKey && w.owner[s] === c.local) c.toggleSelect(id);
      else if (e.ctrlKey || e.metaKey) {
        const same = c.renderer.visibleSlots.filter((o) => w.alive[o] && w.kind[o] === w.kind[s] && w.owner[o] === w.owner[s]).map((o) => w.idOf(o));
        c.setSelection(same, e.shiftKey);
      } else c.setSelection([id]);
      this.lastClick = { t: now, slot: s };
      return;
    }
    // Box select: own units preferred; military over villagers when mixed is not forced (classic behaviour selects all).
    const r = this.rect();
    const x0 = Math.min(start.x, e.clientX) - r.left;
    const x1 = Math.max(start.x, e.clientX) - r.left;
    const y0 = Math.min(start.y, e.clientY) - r.top;
    const y1 = Math.max(start.y, e.clientY) - r.top;
    const v = new THREE.Vector3();
    const units: number[] = [];
    const buildings: number[] = [];
    for (const s of c.renderer.visibleSlots) {
      if (!w.alive[s] || w.owner[s] !== c.local) continue;
      c.renderer.screenPos(s, r.width, r.height, v);
      if (v.x < x0 || v.x > x1 || v.y < y0 || v.y > y1) continue;
      if (w.cat[s] === Cat.Unit) units.push(w.idOf(s));
      else if (w.cat[s] === Cat.Building) buildings.push(w.idOf(s));
    }
    const pick = units.length ? units : buildings.slice(0, 1);
    c.setSelection(pick.slice(0, 200), e.shiftKey);
  }

  private keyDown(e: KeyboardEvent): void {
    const tgt = e.target as HTMLElement;
    if (tgt && (tgt.tagName === 'INPUT' || tgt.tagName === 'TEXTAREA' || tgt.tagName === 'SELECT')) return;
    const c = this.client;
    this.keys.add(e.code);
    const ctrl = e.ctrlKey || e.metaKey;
    // Control groups.
    if (/^Digit[0-9]$/.test(e.code)) {
      const i = Number(e.code.slice(5));
      if (ctrl) {
        c.setGroup(i);
        e.preventDefault();
      } else {
        const now = performance.now();
        const dbl = this.lastGroupTap.i === i && now - this.lastGroupTap.t < 350;
        this.lastGroupTap = { i, t: now };
        c.recallGroup(i, e.shiftKey, dbl);
      }
      return;
    }
    // Camera bookmarks on F5-F8 (F2 and F3 are the performance overlay and pause). The browser's
    // own use of F5 (reload) is suppressed while playing.
    if (/^F[5-8]$/.test(e.code)) {
      const i = Number(e.code.slice(1)) - 5;
      if (ctrl) c.cam.setBookmark(i);
      else c.cam.recallBookmark(i);
      e.preventDefault();
      return;
    }
    // While the build menu is open, letters pick buildings (H house, B barracks, ...).
    if (!ctrl && /^Key[A-Z]$/.test(e.code) && c.buildHotkey(e.code.slice(3))) {
      e.preventDefault();
      return;
    }
    switch (e.code) {
      case 'Enter':
        if (c.transport.networked) {
          useApp.getState().setChatOpen(true);
          e.preventDefault();
        }
        break;
      case 'Escape':
        if (c.mode !== 'normal' || c.buildMenu) c.cancelMode();
        else if (c.selection.size) c.setSelection([]);
        else useApp.getState().setMenuOpen(!useApp.getState().menuOpen);
        break;
      case 'F10':
        useApp.getState().setMenuOpen(!useApp.getState().menuOpen);
        e.preventDefault();
        break;
      case 'Pause':
      case 'F3':
        if (!c.transport.networked) c.paused = !c.paused;
        e.preventDefault();
        break;
      case 'F2':
        useApp.getState().setPrefs({ showFps: !useApp.getState().prefs.showFps });
        e.preventDefault();
        break;
      case 'KeyH':
        c.selectTownCenter();
        break;
      case 'Period':
        c.selectIdleVillager();
        break;
      case 'Space':
        c.jumpToAlert();
        e.preventDefault();
        break;
      case 'Delete':
        c.button('delete');
        break;
      case 'KeyS':
        if (!ctrl) c.button('stop');
        break;
      case 'KeyA':
        c.button('attackMove');
        break;
      case 'KeyP':
        c.button('patrol');
        break;
      case 'KeyG':
        c.button('guard');
        break;
      case 'KeyR':
        c.button('retreat');
        break;
      case 'KeyB': {
        const s = c.primarySlot();
        if (s >= 0 && c.world.cat[s] === Cat.Unit && KINDS[c.world.kind[s]].unit!.classes & C.Villager) c.buildMenu = true;
        break;
      }
      case 'KeyF':
        c.toggleFollow();
        break;
      case 'KeyV': {
        // Town Centre selected: train a villager (Shift: five).
        const s = c.primarySlot();
        if (!ctrl && s >= 0 && c.world.cat[s] === Cat.Building && c.world.owner[s] === c.local && KINDS[c.world.kind[s]].building!.trains?.includes('villager')) {
          for (let i = 0; i < (e.shiftKey ? 5 : 1); i++) c.button('train:villager');
        }
        break;
      }
      case 'Home':
        c.cam.resetView();
        break;
      case 'Equal':
      case 'NumpadAdd':
        if (!c.transport.networked) c.speed = Math.min(4, c.speed * 2);
        break;
      case 'Minus':
      case 'NumpadSubtract':
        if (!c.transport.networked) c.speed = Math.max(0.25, c.speed / 2);
        break;
      default:
        break;
    }
  }

  update(dt: number): void {
    const c = this.client;
    const k = this.keys;
    const speed = 30 * dt * useApp.getState().prefs.scrollSpeed;
    let px = 0;
    let pz = 0;
    if (k.has('ArrowLeft')) px -= speed;
    if (k.has('ArrowRight')) px += speed;
    if (k.has('ArrowUp')) pz -= speed;
    if (k.has('ArrowDown')) pz += speed;
    const st = useApp.getState();
    if (st.prefs.edgeScroll && this.mouse.inWindow && !this.dragStart && !st.menuOpen && !st.chatOpen && document.hasFocus()) {
      const m = 8;
      const w = window.innerWidth;
      const h = window.innerHeight;
      // The top zone is the whole resource/title bar: browsers give no usable pixels above the page.
      const topBar = (document.querySelector('.topbar') as HTMLElement | null)?.getBoundingClientRect().bottom ?? 40;
      if (this.mouse.x <= m) px -= speed;
      if (this.mouse.x >= w - m) px += speed;
      if (this.mouse.y <= Math.max(m, topBar) && !this.mouse.overControl) pz -= speed;
      if (this.mouse.y >= h - m) pz += speed;
    }
    if (px || pz) c.cam.pan(px, pz);
    if (k.has('KeyQ')) c.cam.rotate(dt * 1.5);
    if (k.has('KeyE')) c.cam.rotate(-dt * 1.5);
    if (k.has('PageUp')) c.cam.tilt(dt);
    if (k.has('PageDown')) c.cam.tilt(-dt);
    // Hover highlighting (throttled).
    this.hoverTimer += dt;
    if (this.hoverTimer > 0.08 && this.mouse.inside) {
      this.hoverTimer = 0;
      c.renderer.hovered = this.pickAt(this.mouse.x, this.mouse.y);
      c.canvas.style.cursor = this.cursorFor(c.renderer.hovered);
    }
  }

  private cursorFor(s: number): string {
    const c = this.client;
    if (c.mode === 'attackMove' || c.mode === 'patrol') return 'crosshair';
    if (c.mode === 'place') return 'copy';
    if (s < 0) return 'default';
    const w = c.world;
    if (w.owner[s] >= 0 && w.areEnemies(c.local, w.owner[s]) && c.ownSelectedUnits().length) {
      // Engineers over a building they can take: a flag ("capture") rather than crosshairs.
      if (canCapture(w, c.local, s) && c.selectedEngineers().length) return CAPTURE_CURSOR;
      return 'crosshair';
    }
    return 'pointer';
  }
}
